import { PlusIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '@nextui-org/react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';

import { AddServerSchema, ServerSchema, UpdateServerSchema } from '@common/types';
import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { PasswordInput } from '@portal/components/common/PasswordInput.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { SelectConnectionTypeUI } from '@portal/components/SelectConnectionType';

type BaseServerFormUIProps = {
  modalVisible: boolean;
  onModalVisibleChange: () => void;

  control: Control<AddServerSchema | UpdateServerSchema>;
  register: UseFormRegister<AddServerSchema | UpdateServerSchema>;
  errors?: FieldErrors<AddServerSchema | UpdateServerSchema>;

  isSubmitting: boolean;
  isSubmitValid: boolean;
  onSubmit: () => void;
};

type ServerFormUIProps =
  | (BaseServerFormUIProps & {
      onSubmitAndClose: () => void;
      isUpdate?: false;
    })
  | (BaseServerFormUIProps & {
      isUpdate: true;
    });

export const ServerFormUI = (props: ServerFormUIProps) => {
  const title = props.isUpdate ? 'Edit Server' : 'Add Server';
  const subtitle = props.isUpdate ? 'Update server information' : 'Please provide server information';
  const icon = <PlusIcon className='h-4 w-4' />;

  const footer = (
    <>
      <Button color='danger' variant='light' onPress={props.onModalVisibleChange}>
        Cancel
      </Button>
      {!props.isUpdate && (
        <Button
          color='default'
          type='button'
          variant='bordered'
          isLoading={props.isSubmitting}
          isDisabled={!props.isSubmitValid}
          onPress={props.onSubmitAndClose}
        >
          Add and Close
        </Button>
      )}
      <Button
        color='primary'
        type='button'
        isLoading={props.isSubmitting}
        isDisabled={!props.isSubmitValid}
        onPress={props.onSubmit}
      >
        {!props.isUpdate ? 'Add Another Server' : 'Update Server'}
      </Button>
    </>
  );

  return (
    <ModernModal
      isOpen={props.modalVisible}
      onOpenChange={props.onModalVisibleChange}
      title={title}
      subtitle={subtitle}
      icon={icon}
      size='2xl'
      footer={footer}
    >
      <div className='flex flex-col gap-4'>
        <div className='grid grid-cols-5 gap-2'>
          <div className='col-span-3'>
            <Input
              autoFocus
              label='Server Name'
              variant='bordered'
              {...props.register('name', { required: true })}
              isInvalid={!!props.errors?.name}
              errorMessage={props.errors?.name?.message}
            />
          </div>
          <div className='col-span-2'>
            <Input
              label='Cluster Tag'
              variant='bordered'
              {...props.register('clusterTag')}
              isInvalid={!!props.errors?.clusterTag}
              errorMessage={props.errors?.clusterTag?.message}
            />
          </div>
        </div>
        <Input
          label='Host'
          variant='bordered'
          {...props.register('host')}
          isInvalid={!!props.errors?.host}
          errorMessage={props.errors?.host?.message}
        />
        <div className='grid grid-cols-4 gap-2'>
          <div className='col-span-1'>
            <Input
              type='number'
              label='SSH Port'
              variant='bordered'
              {...props.register('sshPort', { valueAsNumber: true })}
              isInvalid={!!props.errors?.sshPort}
              errorMessage={props.errors?.sshPort?.message}
            />
          </div>
          <div className='col-span-1'>
            <Input
              type='number'
              label='PHP Port'
              variant='bordered'
              {...props.register('phpPort', { valueAsNumber: true })}
              isInvalid={!!props.errors?.phpPort}
              errorMessage={props.errors?.phpPort?.message}
            />
          </div>
          <div className='col-span-2'>
            <Controller
              control={props.control}
              name='connectionType'
              render={({ field: { onChange, value } }) => (
                <SelectConnectionTypeUI onSelectedChange={onChange} value={value} />
              )}
            />
          </div>
        </div>
        <div className='flex flex-row gap-2'>
          <Input
            label='SSH Username'
            variant='bordered'
            {...props.register('sshUsername')}
            isInvalid={!!props.errors?.sshUsername}
            errorMessage={props.errors?.sshUsername?.message}
          />
          <Controller
            control={props.control}
            name='sshPassword'
            render={({ field: { onChange, value } }) => (
              <PasswordInput
                label='SSH Password'
                variant='bordered'
                value={value || ''}
                onValueChange={onChange}
                isInvalid={!!props.errors?.sshPassword}
                errorMessage={props.errors?.sshPassword?.message}
              />
            )}
          />
        </div>
      </div>
    </ModernModal>
  );
};
